import type { TNTAuditInfo, TNTDataSetVersionDto } from '@shipium-corp/shipium-ts-types';

/** Free-form JSON the API round-trips and the UI does not introspect. */
export type JsonObject = Record<string, unknown>;

/** Location of a file in S3, as returned on upload jobs. */
export interface TntFileLocation {
  fileBucket?: string;
  filePath?: string;
}

/** Failure detail carried by an upload job. Mirrors tnt-management's ErrorInfo schema. */
export interface TntErrorInfo {
  errorCode?: string | null;
  errorMessage?: string | null;
  invalidRowCount?: number;
  parseErrorCounts?: { count?: number; errorDetail?: string; errorLocation?: string }[];
  errorFileLocation?: TntFileLocation | null;
}

export interface TntDataSetTypesSearchBody {
  destinationPostalSearch?: {
    postalLength: number;
  };
  expirationTimestampThreshold?: string;
  metadataSearch?: JsonObject;
  originPostalSearch?: {
    postalLength: number;
  };
  tntSourceName?: string;
}

export const tntUsageTypeToApi: Record<TntType, TntUsageType> = {
  production: 'PROD',
  simulation: 'SIMULATION',
  carrier_default: 'PROD',
  promise: 'PROD',
};

// Keyed by TntUsageType ('PROD' | 'SIMULATION'), not TntType, so adding a new
// TntType ('promise') does not require a change here — it's a lossy reverse
// map already (carrier_default also maps to PROD but reverses to 'production').
export const tntApiToUsageType: Record<TntUsageType, TntType> = {
  PROD: 'production',
  SIMULATION: 'simulation',
};

type PresenceType = 'ABSENT' | 'OPTIONAL' | 'REQUIRED';

export type TntUsageType = 'PROD' | 'SIMULATION';

export type TntType = 'production' | 'simulation' | 'carrier_default' | 'promise';

export interface TntDataSetType {
  active: boolean;
  usage: TntUsageType;
  auditInfo: {
    createdAt: string;
    createdBy: string;
    lastUpdatedBy: string;
    updatedAt: string;
  };
  dataSetMetadataDefinition?: {
    additionalProp1: {
      dataType: boolean | number | string;
      required: boolean;
    };
    additionalProp2: {
      dataType: boolean | number | string;
      required: boolean;
    };
    additionalProp3: {
      dataType: boolean | number | string;
      required: boolean;
    };
  };
  deletionTimestamp: string;
  description: string;
  expirationTimestamp: string;
  id: string;
  metadata: JsonObject;
  name: string;
  recordMetadataDefinition?: {
    additionalProp1: {
      dataType: boolean | number | string;
      required: boolean;
    };
    additionalProp2: {
      dataType: boolean | number | string;
      required: boolean;
    };
    additionalProp3: {
      dataType: boolean | number | string;
      required: boolean;
    };
  };
  recordRouteDefinition: {
    originDestination: {
      destination: {
        country: PresenceType;
        postal: {
          postalLength: 0;
          presence: PresenceType;
        };
        postalCode: PresenceType;
      };
      origin: {
        country: PresenceType;
        postal: {
          postalLength: number;
          presence: PresenceType;
        };
        postalCode: PresenceType;
      };
    };
    shippingZone: {
      zoneId: PresenceType;
    };
  };
  scope: {
    carrierId: string;
    partnerId: string;
  };
  tntSourceId: string;
}

export interface TntDataSetResponse {
  dataSets: TntDataSet[];
}

export interface TntDataSet {
  dataSetTypeId: string;
  deletedAt: string;
  description: string;
  dataSetName: string;
  id: string;
  metadata: JsonObject;
  writeCompletedAt: string;
  versionNumber: number;
  scope?: {
    carrierId?: string;
  };
}

export interface DataSetResponseV2 {
  id: string;
  name: string;
  scope: {
    partnerId: string;
    carrierId: string;
    restrictedToCarrierScopes: string[];
  };
  description: string;
  tntSource: string;
  dataSetMetadataDefinition: JsonObject;
  recordMetadataDefinition: JsonObject;
  deletionTimestamp: string;
  recordRouteDefinition: JsonObject;
  usage: string;
  metadata: JsonObject;
  auditInfo: TNTAuditInfo;
  dataSetVersions?: TNTDataSetVersionDto[];
}

export interface TntDataSetV2 {
  uploadJobId: string;
  uploadJobStatus: string;
  tntDataSetDescription: string;
  tntDataSetName: string;
  tntDataSetId: string;
  tntDataSetVersionId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TntDataSetsV2 {
  dataSets: TntDataSetV2[];
}

export interface TntJobsV2 {
  jobs: TntJobV2[];
}

export interface TntJob {
  uploadJobId: string;
  uploadJobStatus: 'IN_PROGRESS' | 'COMPLETE' | 'FAILED';
  dataSetTypeId: string;
  errorMessage: string;
  createdAt: string;
  updatedAt: string;
  name?: string;
  description?: string;
}

export interface TntJobV2 {
  uploadJobId: string;
  uploadJobStatus: 'IN_PROGRESS' | 'COMPLETE' | 'FAILED';
  tntDataSetId: string;
  tntDataSetVersionId: string;
  tntDataSetDescription?: string;
  tntDataSetName?: string;
  usage: string;
  errorInfo: TntErrorInfo;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  dataSets?: unknown;
  tntJobs?: unknown;
  versionNumber?: number;
  fileTntSource?: string;
  carrierId?: string;
}

export interface TntSource {
  id: string;
  name: string | 'partner-provided-postal' | 'partner-provided-shipments-statistics' | 'shipium-shipments-statistics';
  description: string;
  tntDimensionsDefinition: {
    tntByDimension: {
      [key: string | 'DEFAULT' | 'AVG']: {
        required: boolean;
      };
    };
  };
}
export type TntRecordsResponse = JsonObject;
export type DownloadTntResponse = string;
export interface DownloadTntRecordsRequest {
  carrierServiceMethodIdsSearch?: string[];
  dataSetTypeId?: string;
  destinationCountrySearch?: string[];
  destinationLocationSearch?: {
    countryCode?: string;
    postalCode?: string;
  }[];
  originCountrySearch?: string[];
  originLocationSearch?: {
    countryCode?: string;
    postalCode?: string;
  }[];
}
export interface DownloadTntRecordsRequestV2 {
  dataSetId?: string;
  dataSetVersionId?: string;
  originLocations?: {
    countryCode?: string;
    postalCode?: string;
  }[];
  destinationLocations?: {
    countryCode?: string;
    postalCode?: string;
  }[];
  carrierServiceMethodIds?: string[];
}
export interface UploadProcessTntRecordsRequest {
  applyUpload?: boolean;
  tntDataSetDescription?: string;
  tntDataSetName: string;
  expiresAt?: string;
  metadata?: JsonObject;
  tntRecordsFilePath: string;
  tntRecordsS3Bucket: string;
  usage: TntUsageType;
}
export interface UploadTntResponse {
  dataSetType: TntDataSetType;
  invalidTntRecords?: {
    invalidColumnMessages: string[];
    rowNumber: number;
  };
  numTntRecords: number;
  uploadApplied: boolean;
}
export interface UpdateDataSetTypeFromUploadRequest {
  applyUpload: boolean;
  tntRecordsFilePath: string;
  tntRecordsS3Bucket: string;
}
export interface CreateDataSet {
  dataSetTypeId: string;
  metadata?: JsonObject;
  description?: string;
  dataSetName: string;
}

export interface UploadDataSet {
  dataSetTypeId: string;
  tntRecordsS3Bucket: string;
  tntRecordsFilePath: string;
  applyUpload: boolean;
}

export interface EditDataSet {
  dataSetId: string;
  metadata?: JsonObject;
  description: string;
}

export interface CreateDataSetResponse {
  dataSetTypeId: string;
  metadata?: JsonObject;
  description: string;
  dataSetName: string;
  id: string;
}

export interface GetTntStatusResponse {
  dataSetTypeId: string;
  activationStatus: string;
}

export interface UploadProcessTntRecordsRequestV2 {
  tntDataSetDescription?: string;
  tntDataSetName: string;
  tntRecordsFileLocation: {
    fileBucket: string;
    filePath: string;
  };
  fileTntSource?: string;
  scope?: {
    carrierId: string;
  };
  metadata?: JsonObject;
  usage: TntUsageType;
  applyUpload: boolean;
}

export interface UpdateDataSetTypeFromUploadRequestV2 {
  tntDataSetId: string;
  tntDataSetName: string;
  tntDataSetDescription: string;
  tntRecordsFileLocation: {
    fileBucket: string;
    filePath: string;
  };
  applyUpload: boolean;
}

export interface UploadProdTntResponse {
  uploadJobId: string;
  uploadJobStatus: string;
  usage: string;
  tntDataSetId: string;
  tntDataSetVersionId: string;
  dataSetId: string;
  errorInfo: TntErrorInfo;
  createdAt: string;
  updatedAt: string;
}
